import {
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { TrackedFile } from '../hooks/useReceiptUpload';

interface FileListItemProps {
  trackedFile: TrackedFile;
  onRemove: (id: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const getStatusText = (trackedFile: TrackedFile): string => {
  switch (trackedFile.status) {
    case 'pending':
      return 'Waiting...';
    case 'uploading':
      return `Uploading ${trackedFile.uploadProgress}%`;
    case 'creating':
      return 'Creating receipt...';
    case 'done':
      return 'Ready';
    case 'error':
      return trackedFile.error || 'Failed';
  }
};

const getStatusColor = (status: TrackedFile['status']): string => {
  switch (status) {
    case 'pending':
      return 'text-gray-500 dark:text-gray-400';
    case 'uploading':
    case 'creating':
      return 'text-blue-600 dark:text-blue-400';
    case 'done':
      return 'text-green-600 dark:text-green-400';
    case 'error':
      return 'text-red-600 dark:text-red-400';
  }
};

const FileListItem: React.FC<FileListItemProps> = ({
  trackedFile,
  onRemove,
}) => {
  const isActive = trackedFile.status === 'uploading' || trackedFile.status === 'creating';
  const canRemove = trackedFile.status === 'pending' || trackedFile.status === 'error' || trackedFile.status === 'done';

  const icon = (() => {
    switch (trackedFile.status) {
      case 'done':
        return <CheckCircleIcon className='w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0' />;
      case 'error':
        return <ExclamationCircleIcon className='w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0' />;
      default:
        return <DocumentTextIcon className='w-5 h-5 text-red-500 flex-shrink-0' />;
    }
  })();

  return (
    <div className='p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-3 flex-1 min-w-0'>
          {icon}
          <div className='flex-1 min-w-0'>
            <p className='text-sm text-gray-900 dark:text-gray-100 truncate'>
              {trackedFile.file.name}
            </p>
            <p className={`text-xs ${getStatusColor(trackedFile.status)}`}>
              {formatFileSize(trackedFile.file.size)} - {getStatusText(trackedFile)}
            </p>
          </div>
        </div>
        {canRemove && (
          <button
            onClick={e => {
              e.stopPropagation();
              onRemove(trackedFile.id);
            }}
            className='ml-3 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200'
            aria-label={`Remove ${trackedFile.file.name}`}
          >
            <XMarkIcon className='w-5 h-5 text-gray-500 dark:text-gray-400' />
          </button>
        )}
      </div>
      {isActive && (
        <div className='mt-2'>
          <div className='w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5'>
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${
                trackedFile.status === 'creating' ? 'bg-blue-600 animate-pulse' : 'bg-blue-600'
              }`}
              style={{ width: `${trackedFile.status === 'creating' ? 100 : trackedFile.uploadProgress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileListItem;
